import { query } from 'bitecs';
import { Config } from '@src/core/Config';
import { scaleScore } from '@src/core/Difficulty';
import { GameEvent } from '@src/core/EventQueue';
import { giveShield, giveSwift, giveWeapon, kill, PrizeKind } from '@src/ecs/archetypes';
import { Dying, Health, Pickup, PickupKind, Player, Position, Radius, Sprite, Velocity, Weapon } from '@src/ecs/components';
import type { GameSystem } from '@src/ecs/System';
import { emitParticles, emitPopup, ParticleKind, PopupKind, type GameContext } from '@src/ecs/world';

const PRIZE_SCORE: Readonly<Record<PrizeKind, number>> = {
  [PrizeKind.Cabbage]: 1500,
  [PrizeKind.Hat]: 1000,
  [PrizeKind.Chalice]: 1200,
  [PrizeKind.GoldChest]: 2500,
  [PrizeKind.Spellbook]: 1800,
  [PrizeKind.CrystalBall]: 1400,
  [PrizeKind.Potion]: 800,
  [PrizeKind.Hourglass]: 2000,
};
const GEM_SCORE = 25;
/** Gems grabbed inside this window count toward the greed streak. */
const GREED_WINDOW = 5;
const GREED_COUNT = 10;

/**
 * Gems magnetize toward players and add to the shared score (times the combo
 * multiplier). Epic Weapons are single-claim: the first player to touch one takes
 * it and it vanishes. Grabbing a duplicate of the held weapon resets its timer and
 * triggers Overcharge.
 */
export class PickupSystem implements GameSystem {
  private ctx!: GameContext;
  private greedStart = 0;
  private greedCount = 0;
  private greedFired = false;
  private trailAccum = 0;

  init(ctx: GameContext): void {
    this.ctx = ctx;
  }

  destroy(): void {}

  update(dt: number): void {
    const ctx = this.ctx;
    const players = query(ctx.world, [Player, Position, Radius, Weapon]);
    const pickups = query(ctx.world, [Pickup, Position, Radius, Velocity, Sprite]);
    const magnet = Config.player.magnetRadius;

    this.trailAccum += dt;
    const trail = this.trailAccum >= 0.05;
    if (trail) this.trailAccum = 0;

    for (let i = 0; i < pickups.length; i++) {
      const pk = pickups[i];
      if (Dying.flag[pk]) continue;

      // Supply drops leave a sparkle wake while they glide in.
      if (trail && Pickup.drift[pk] > 0) {
        const heart = Pickup.kind[pk] === PickupKind.Heart;
        emitParticles(ctx.fx, ParticleKind.PickupSparkle, Position.x[pk], Position.y[pk], -Velocity.x[pk], -Velocity.y[pk], 0.35, heart ? 0xff7a9a : 0xfff0a0);
      }

      for (let p = 0; p < players.length; p++) {
        const pid = players[p];
        if (Player.dead[pid]) continue;
        const dx = Position.x[pid] - Position.x[pk];
        const dy = Position.y[pid] - Position.y[pk];
        const d2 = dx * dx + dy * dy;
        const rr = Radius.r[pid] + Radius.r[pk];

        const magnetic = Pickup.kind[pk] === PickupKind.Gem || (Pickup.kind[pk] === PickupKind.Heart && Pickup.drift[pk] <= 0);
        if (magnetic && d2 < magnet * magnet && d2 > rr * rr) {
          const d = Math.sqrt(d2) || 1;
          const pull = Config.pickups.gemMagnetSpeed * (1 - d / magnet) + 3;
          Velocity.x[pk] += ((dx / d) * pull - Velocity.x[pk]) * Math.min(1, dt * 10);
          Velocity.y[pk] += ((dy / d) * pull - Velocity.y[pk]) * Math.min(1, dt * 10);
        }

        if (d2 > rr * rr) continue;
        this.collect(pid, pk);
        break;
      }
    }
  }

  private collect(pid: number, pk: number): void {
    const ctx = this.ctx;
    const x = Position.x[pk];
    const y = Position.y[pk];
    switch (Pickup.kind[pk]) {
      case PickupKind.Gem: {
        const value = scaleScore(Pickup.sub[pk] * GEM_SCORE * ctx.room.tier * ctx.combo.multiplier);
        ctx.score += value;
        ctx.events.push(GameEvent.PickupGem, pid, value, x, y);
        emitParticles(ctx.fx, ParticleKind.PickupSparkle, x, y, 0, 1, 0.3, 0x8dffc8);
        if (ctx.time - this.greedStart > GREED_WINDOW) {
          this.greedStart = ctx.time;
          this.greedCount = 0;
          this.greedFired = false;
        }
        this.greedCount++;
        if (this.greedCount >= GREED_COUNT && !this.greedFired) {
          this.greedFired = true;
          ctx.events.push(GameEvent.GemGreed, this.greedCount, 0, x, y);
        }
        break;
      }
      case PickupKind.Prize: {
        const kind = Pickup.sub[pk] as PrizeKind;
        const value = scaleScore((PRIZE_SCORE[kind] ?? 1000) * ctx.combo.multiplier);
        ctx.score += value;
        ctx.events.push(GameEvent.PickupPrize, pid, kind, x, y);
        emitPopup(ctx.fx, PopupKind.Prize, value, x, y + 0.6);
        emitParticles(ctx.fx, ParticleKind.Confetti, x, y, 0, 1, 1);
        Sprite.pop[pid] = 0.5;
        break;
      }
      case PickupKind.Heart: {
        const before = Health.hp[pid];
        Health.hp[pid] = Math.min(Health.max[pid], before + Pickup.sub[pk]);
        const healed = Math.round(Health.hp[pid] - before);
        ctx.events.push(GameEvent.PickupHeart, pid, healed, x, y);
        emitPopup(ctx.fx, PopupKind.Heal, healed, x, y + 0.6);
        emitParticles(ctx.fx, ParticleKind.PickupSparkle, x, y, 0, 1, 1.0, 0xff6a8a);
        emitParticles(ctx.fx, ParticleKind.Embers, Position.x[pid], Position.y[pid], 0, 1, 0.6, 0xff9ab0);
        Sprite.pop[pid] = 0.45;
        Sprite.flash[pid] = 0.6;
        break;
      }
      case PickupKind.Weapon: {
        const weapon = Pickup.sub[pk];
        emitParticles(ctx.fx, ParticleKind.PickupSparkle, x, y, 0, 1, 1.2, 0xffffff);
        Sprite.pop[pid] = 0.8;
        if (Weapon.typeId[pid] === weapon) {
          giveWeapon(pid, weapon);
          Weapon.overcharge[pid] = Config.pickups.overchargeSeconds;
          ctx.events.push(GameEvent.Overcharge, pid, weapon, x, y);
          ctx.fx.hitStopMs = Math.max(ctx.fx.hitStopMs, 90);
          ctx.fx.flash = Math.max(ctx.fx.flash, 0.5);
          ctx.fx.rumble = 1;
        } else {
          giveWeapon(pid, weapon);
          ctx.events.push(GameEvent.PickupWeapon, pid, weapon, x, y);
        }
        break;
      }
      case PickupKind.Shield: {
        giveShield(pid);
        emitParticles(ctx.fx, ParticleKind.PickupSparkle, x, y, 0, 1, 1.2, Config.pickups.shield.pickupTint);
        emitParticles(ctx.fx, ParticleKind.Embers, Position.x[pid], Position.y[pid], 0, 1, 0.7, Config.pickups.shield.tint);
        Sprite.pop[pid] = 0.7;
        Sprite.flash[pid] = 0.75;
        ctx.events.push(GameEvent.PickupShield, pid, 0, x, y);
        break;
      }
      case PickupKind.Swift: {
        giveSwift(pid);
        emitParticles(ctx.fx, ParticleKind.PickupSparkle, x, y, 0, 1, 1.1, Config.pickups.swift.pickupTint);
        emitParticles(ctx.fx, ParticleKind.Embers, Position.x[pid], Position.y[pid], 0, 1, 0.8, Config.pickups.swift.tint);
        Sprite.pop[pid] = 0.55;
        Sprite.flash[pid] = 0.65;
        ctx.events.push(GameEvent.PickupSwift, pid, 0, x, y);
        break;
      }
      default:
        break;
    }
    kill(pk);
  }
}
